import React from 'react';
import {
  Button,
  RefreshIcon,
  Table,
  TablePagination,
  TableToolbar,
  TableToolbarActions,
  TableBody,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TablePaginationProps,
} from '@razorpay/blade/components';
import { ErrorView } from 'components/ui/ErrorView/ErrorView';
import { EmptyList } from './components/EmptyList';
import { EmployeeTableRow } from './components/EmployeeTableRow';
import { usePagination } from 'hooks/usePagination';
import { UseQueryResult } from 'react-query';
import { EmployeeTableNode, PeopleTabValue } from './types';
import { formatPaginationRange } from 'components/ApprovalWorkflows/MyRequests/EmployeeBankAccount/utils';
import { getPeopleTableHeading, showsEmployeeTypeColumn } from './components/data';
import useReduxSelector from 'utils/useReduxSelector';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { getContractorText } from 'utils/getContractorText';
import { useEmployeeSubTypeEnabled } from 'hooks/useEmployeeSubTypeEnabled';

export const PeopleList = ({
  queryResponse,
  employees,
  totalCount,
  paginationType = 'server',
  activeTab,
}: {
  queryResponse: UseQueryResult<any>;
  employees: EmployeeTableNode[] | undefined;
  activeTab: PeopleTabValue;
  totalCount: number;
  paginationType?: 'server' | 'client';
}) => {
  const isEmployeeSubTypeEnabled = useEmployeeSubTypeEnabled();
  const showEmploymentType = isEmployeeSubTypeEnabled && showsEmployeeTypeColumn(activeTab);
  const isRxCurrentAccountMerchant = !!useReduxSelector(loggedInUserSelectors.currentOrganization)
    ?.isRxCurrentAccountMerchant;
  const featureFlags = useReduxSelector(loggedInUserSelectors.features);

  const { contractors } = getContractorText({
    isRxCurrentAccountMerchant,
    featureFlags,
  });
  const { isLoading, isFetching, isError, refetch } = queryResponse;
  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });

  const onPageChange = ({ page }: { page: number }) => {
    setCurrentPage(page);
  };

  const onPageSizeChange = ({ pageSize }: { pageSize: number }) => {
    setItemsPerPage(pageSize);
    setCurrentPage(0);
  };

  const tableTitle =
    employees && employees.length > 0
      ? `Showing ${formatPaginationRange({
          totalItems: totalCount,
          currentPage,
          itemsPerPage,
        })} ${getPeopleTableHeading(contractors)[activeTab]}`
      : '';

  if (isError) {
    return (
      <ErrorView
        onRefetch={refetch}
        borderWidth="none"
        height="auto"
        backgroundColor="surface.background.gray.intense"
      />
    );
  }

  if (!isLoading && !isFetching && !employees?.length) {
    return <EmptyList activeTab={activeTab} />;
  }

  return (
    <Table
      data={{
        nodes: employees || [],
      }}
      isLoading={isLoading}
      isRefreshing={isFetching}
      pagination={
        <TablePagination
          currentPage={currentPage}
          defaultPageSize={itemsPerPage as TablePaginationProps['defaultPageSize']}
          paginationType={paginationType}
          showPageSizePicker
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          totalItemCount={totalCount}
        />
      }
      toolbar={
        <TableToolbar title={tableTitle}>
          <TableToolbarActions>
            <Button
              icon={RefreshIcon}
              variant={'tertiary'}
              accessibilityLabel="Refresh"
              onClick={refetch}
            />
          </TableToolbarActions>
        </TableToolbar>
      }>
      {(tableData) => (
        <>
          <TableHeader>
            <TableHeaderRow>
              <TableHeaderCell>Name</TableHeaderCell>
              {showEmploymentType && <TableHeaderCell>Employee Type</TableHeaderCell>}
              <TableHeaderCell>Designation</TableHeaderCell>
              <TableHeaderCell>Department</TableHeaderCell>
            </TableHeaderRow>
          </TableHeader>

          <TableBody>
            {tableData.map((employee) => (
              <EmployeeTableRow
                key={employee.id}
                employee={employee}
                showEmploymentType={showEmploymentType}
              />
            ))}
          </TableBody>
        </>
      )}
    </Table>
  );
};
